import {
  AriaAttributes,
  ReactElement,
  Ref,
  forwardRef,
  useId,
  useMemo,
} from "react";
import { DataTrackingId } from "../../types";
import styles from "./DaysOfTheWeek.module.scss";
import { FieldLabel, FieldLabelProps } from "../FieldLabel";

import ButtonToggle from "../ButtonToggle";
import Tooltip from "../Tooltip";
import { Helper } from "../../internal/components";
import Flex from "../Flex";

/**
 * Props for the DaysOfTheWeek component
 */
export type DaysOfTheWeekProps = {
  /**
   * The selected days (1-7, where 1 is Monday and 7 is Sunday)
   */
  selectedDays?: number[];
  /**
   * Callback fired when the selection changes
   */
  onChange?: (selectedDays: number[]) => void;
  /**
   * Whether the component is disabled
   * Can be a boolean to disable all days, or an array of day numbers (1-7) to disable specific days
   */
  disabled?: boolean | number[];
  /**
   * The first day of the week (1-7, where 1 is Monday and 7 is Sunday)
   * @default 1
   */
  firstDay?: number;
  /**
   * Hint text or element to display above the component
   */
  hint?: ReactElement | string;
  /**
   * ID for the component
   */
  id?: string;
  /**
   * Description text or element to display below the component
   */
  description?: ReactElement | string;
  /**
   * Error message(s) to display. Supports a single string or an array of strings.
   */
  error?: string | string[];
  /**
   * Warning message(s) to display. Supports a single string or an array of strings.
   */
  warning?: string | string[];
  /**
   * @deprecated Use the `error` prop instead.
   */
  errorMessage?: ReactElement | string;
  /**
   * @deprecated No longer used. Error messages always use `aria-live="assertive"`.
   */
  errorAriaLive?: AriaAttributes["aria-live"];
  /**
   * Label text or element
   */
  label?: FieldLabelProps["children"];
  /**
   * Whether the field is required
   */
  required?: boolean;
  /**
   * Additional info to display in a tooltip next to the label
   */
  moreInfo?: FieldLabelProps["moreInfo"];
  /**
   * Additional props to pass to the Label component
   */
  labelProps?: FieldLabelProps;
} & DataTrackingId;

interface DayInfo {
  short: string;
  full: string;
}

// Days in order from Monday to Sunday
const DAYS: DayInfo[] = [
  { short: "Mo", full: "Monday" },
  { short: "Tu", full: "Tuesday" },
  { short: "We", full: "Wednesday" },
  { short: "Th", full: "Thursday" },
  { short: "Fr", full: "Friday" },
  { short: "Sa", full: "Saturday" },
  { short: "Su", full: "Sunday" },
];

/**
 * DaysOfTheWeek component for selecting multiple days of the week.
 *
 * Features:
 * - Displays days of the week as toggleable buttons
 * - Supports custom first day of the week (Monday-Sunday)
 * - Individual day selection and deselection
 * - Optional label, hint, description, and error messaging
 * - Full accessibility support with ARIA attributes
 * - Tooltips showing full day names on hover
 * - Responsive design with flexible layout
 * - Support for disabling specific days or the entire component
 * - Automatic tracking ID generation for analytics
 *
 * @example
 * <DaysOfTheWeek
 *   selectedDays={[2, 4, 6]}
 *   onChange={(days) => console.log('Selected days:', days)}
 *   label="Working Days"
 *   firstDay={1}
 * />
 */
export const DaysOfTheWeek = forwardRef(
  (props: DaysOfTheWeekProps, ref: Ref<HTMLDivElement>) => {
    const {
      selectedDays = [],
      onChange,
      disabled,
      firstDay = 7,
      hint,
      id: idProp,
      description,
      error,
      warning,
      errorMessage,
      errorAriaLive,
      label,
      required,
      moreInfo,
      labelProps,
    } = props;

    const autoId = useId();
    const id = idProp ?? autoId;
    const labelUid = useId();
    const helperUid = useId();

    const hasHelperText =
      hint || error || warning || errorMessage || description;

    const orderedDays = useMemo(() => {
      const firstDayIndex = firstDay - 1;
      return [...DAYS.slice(firstDayIndex), ...DAYS.slice(0, firstDayIndex)];
    }, [firstDay]);

    const handleDayToggle = (index: number) => {
      // Convert from 0-based array index to 1-based day number, accounting for firstDay
      const dayNumber = ((index + firstDay - 1) % 7) + 1;
      const newSelectedDays = selectedDays.includes(dayNumber)
        ? selectedDays.filter((d) => d !== dayNumber)
        : [...selectedDays, dayNumber];

      onChange?.(newSelectedDays);
    };

    const isDayDisabled = (dayNumber: number) => {
      if (typeof disabled === "boolean") {
        return disabled;
      }
      if (Array.isArray(disabled)) {
        return disabled.includes(dayNumber);
      }
      return false;
    };

    return (
      <div className={styles["days-of-the-week-container"]}>
        {label ? (
          <FieldLabel
            moreInfo={moreInfo}
            required={required}
            id={labelUid}
            htmlFor={id}
            {...labelProps}
          >
            {label}
          </FieldLabel>
        ) : null}
        <Flex ref={ref} gap={2} className={styles["days-of-the-week"]} id={id}>
          {orderedDays.map((day, index) => {
            // Convert from 0-based array index to 1-based day number, accounting for firstDay
            const dayNumber = ((index + firstDay - 1) % 7) + 1;
            return (
              <Tooltip key={day.short} placement="top">
                <Tooltip.Trigger className={styles["button-toggle-wrapper"]}>
                  <ButtonToggle
                    checked={selectedDays.includes(dayNumber)}
                    onClick={() => handleDayToggle(index)}
                    disabled={isDayDisabled(dayNumber)}
                    className={styles["button-toggle"]}
                    id={label ? labelUid : undefined}
                    aria-describedby={hasHelperText ? helperUid : undefined}
                  >
                    {day.short}
                  </ButtonToggle>
                </Tooltip.Trigger>
                <Tooltip.Content>{day.full}</Tooltip.Content>
              </Tooltip>
            );
          })}
        </Flex>
        {hasHelperText ? (
          <Helper
            id={helperUid}
            hint={hint}
            description={description}
            error={error}
            warning={warning}
            errorMessage={errorMessage}
            errorAriaLive={errorAriaLive}
          />
        ) : null}
      </div>
    );
  },
);
DaysOfTheWeek.displayName = "DaysOfTheWeek";
